import L from 'leaflet';

/**
 * Comprehensive collection of Leaflet basemap providers
 */
export const basemaps = {
  // OpenStreetMap variants
  'OSM Standard': {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19
  },
  'OSM HOT': {
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hotosm.org/">HOT</a>',
    maxZoom: 19
  },
  
  // CARTO variants
  'CARTO Dark': {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20
  },
  'CARTO Light': {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20
  },
  'CARTO Voyager': {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20
  },
  
  // Esri variants
  'Esri WorldImagery': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19
  },
  'Esri Imagery + Roads': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
    overlay: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'
  },
  'Esri WorldStreetMap': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19
  },
  'Esri WorldTopoMap': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community',
    maxZoom: 19
  },
  'Esri NatGeoWorldMap': {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; National Geographic, Esri, DeLorme, NAVTEQ, UNEP-WCMC, USGS, NASA, ESA, METI, NRCAN, GEBCO, NOAA, iPC',
    maxZoom: 16
  },
  
  // OpenTopoMap
  'OpenTopoMap': {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
    maxZoom: 17
  },
  
  // CyclOSM
  'CyclOSM': {
    url: 'https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
    attribution: '<a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" title="CyclOSM - Open Bicycle render">CyclOSM</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 20
  },
  
  // Humanitarian
  'Humanitarian': {
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hotosm.org/" target="_blank">Humanitarian OpenStreetMap Team</a>',
    maxZoom: 19
  }
};

/**
 * Creates and manages the basemap layer control
 */
export class BasemapSelector {
  constructor(map, defaultBasemap = 'Esri Imagery + Roads', onBasemapChange = null) {
    this.map = map;
    this.currentLayer = null;
    this.currentOverlay = null;
    this.currentBasemap = defaultBasemap;
    this.onBasemapChange = onBasemapChange; // Callback for when basemap changes
    
    // Initialize with default basemap
    this.setBasemap(defaultBasemap);
    
    // Create UI
    this.createUI();
  }
  
  /**
   * Sets the active basemap
   */
  setBasemap(name) {
    const config = basemaps[name];
    if (!config) {
      console.error(`Basemap "${name}" not found`);
      return;
    }
    
    // Remove current layers
    if (this.currentLayer) {
      this.map.removeLayer(this.currentLayer);
    }
    if (this.currentOverlay) {
      this.map.removeLayer(this.currentOverlay);
      this.currentOverlay = null;
    }
    
    // Add new base layer
    this.currentLayer = L.tileLayer(config.url, config);
    this.currentLayer.addTo(this.map);
    
    // Update current basemap name
    this.currentBasemap = name;
    
    // Call callback if provided
    if (this.onBasemapChange) {
      this.onBasemapChange(name);
    }
    
    // Add overlay if exists (for hybrid maps with subtle opacity)
    if (config.overlay) {
      this.currentOverlay = L.tileLayer(config.overlay, {
        attribution: '',
        maxZoom: config.maxZoom,
        opacity: 0.4 // Make roads much more subtle
      });
      this.currentOverlay.addTo(this.map);
    }
    
    this.currentBasemap = name;
    
    // Update UI
    this.updateUI();
  }
  
  /**
   * Creates the basemap selector UI
   */
  createUI() {
    const container = document.createElement('div');
    container.className = 'basemap-selector';
    container.innerHTML = `
      <button class="basemap-toggle" id="basemap-toggle">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"/>
        </svg>
        <span id="current-basemap">${this.currentBasemap}</span>
      </button>
      <div class="basemap-menu" id="basemap-menu" style="display: none;">
        ${Object.keys(basemaps).map(name => `
          <button class="basemap-option ${name === this.currentBasemap ? 'active' : ''}" data-basemap="${name}">
            ${name}
          </button>
        `).join('')}
      </div>
    `;
    
    document.body.appendChild(container);
    
    // Add event listeners
    const toggle = document.getElementById('basemap-toggle');
    const menu = document.getElementById('basemap-menu');
    
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
    });
    
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        menu.style.display = 'none';
      }
    });
    
    // Basemap selection
    container.querySelectorAll('.basemap-option').forEach(button => {
      button.addEventListener('click', () => {
        const basemapName = button.getAttribute('data-basemap');
        this.setBasemap(basemapName);
        menu.style.display = 'none';
      });
    });
  }
  
  /**
   * Updates the UI to reflect current basemap
   */
  updateUI() {
    const currentBasemapEl = document.getElementById('current-basemap');
    if (currentBasemapEl) {
      currentBasemapEl.textContent = this.currentBasemap;
    }
    
    // Update active state
    document.querySelectorAll('.basemap-option').forEach(button => {
      const basemapName = button.getAttribute('data-basemap');
      if (basemapName === this.currentBasemap) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    });
  }
}
